<template>
  <Fragment>
    <TextField
      v-model="dateTimeFormatted"
      :title="title"
      :title-hover="titleHover"
      :placeholder="internalPlaceholder"
      :mark="dateInvalid || mark"
      v-bind="$attrs"
    />
    <AutoComplete v-if="!utc && allowTimezoneChange" v-model="timezoneOffset" class="mt-2" :items="timezones" />
  </Fragment>
</template>

<script lang="ts">
import { AutoCompleteElement } from '@anschuetz-elog/common';
import moment from 'moment';
import { computed, defineComponent, PropType, ref, toRef } from 'vue';

import TextField from '#/components/TextField.vue';
import useFeathers from '#/compositions/useFeathers';
import { getCurrentShipsMeanTimeOffset } from '#/compositions/useProtocols';
import { MOMENT_DATE_TIME, MOMENT_FORMATS } from '#/config/time';
import { format } from '#/utilities';

import AutoComplete from './AutoComplete.vue';

export default defineComponent({
  name: 'DateTimeField',

  components: {
    TextField,
    AutoComplete,
  },

  inheritAttrs: false,

  props: {
    // eslint-disable-next-line vue/require-default-prop
    title: {
      type: String,
      required: false,
    },
    titleHover: {
      type: String,
      default: undefined,
    },
    placeholder: {
      type: String,
      default: '',
    },
    // eslint-disable-next-line vue/require-default-prop
    value: {
      type: String,
    },
    format: {
      type: String as PropType<keyof typeof MOMENT_FORMATS | undefined>,
      default: undefined,
    },

    customFormat: {
      type: String as PropType<string | undefined>,
      default: undefined,
    },

    required: {
      type: Boolean,
    },
    disableRounding: {
      type: Boolean,
    },
    mark: {
      type: Boolean,
    },
    utc: {
      type: Boolean,
    },
    allowTimezoneChange: {
      type: Boolean,
    },
  },

  emits: {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    input: (_value: string | undefined): boolean => true,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    invalid: (_valid: boolean): boolean => true,
  },

  setup(props, context) {
    const propFormat = toRef(props, 'format');
    const customFormat = toRef(props, 'customFormat');
    const placeholder = toRef(props, 'placeholder');
    const value = toRef(props, 'value');
    const required = toRef(props, 'required');
    const disableRounding = toRef(props, 'disableRounding');
    const utc = toRef(props, 'utc');
    const allowTimezoneChange = toRef(props, 'allowTimezoneChange');

    const dateInvalid = ref<boolean>(false);

    const momentFormat = computed<string>(() => {
      if (propFormat.value) {
        return MOMENT_FORMATS[propFormat.value];
      } else if (customFormat.value) {
        return customFormat.value;
      }
      return MOMENT_DATE_TIME;
    });
    const internalPlaceholder = computed<string>(() => {
      if (placeholder.value) {
        return placeholder.value;
      }
      return momentFormat.value;
    });

    const smtOffset = getCurrentShipsMeanTimeOffset();
    const timezoneOverwrite = ref<number>();

    function updateDate(date: string, timezone: number) {
      if (!required.value && date === '') {
        dateInvalid.value = false;
        context.emit('input', undefined);
        context.emit('invalid', false);
        return;
      }
      let newDate = moment(date, momentFormat.value, true);
      if (!newDate.isValid()) {
        newDate = moment(moment(date, 'DD-MM-YYYY HH:mm', true), momentFormat.value, true);
      }
      dateInvalid.value = !newDate.isValid();
      if (!dateInvalid.value) {
        context.emit(
          'input',
          import.meta.env.VITE_SMT_HANDLING_ENABLED
            ? newDate.utcOffset(timezone, true).format()
            : newDate.utc().format(),
        );
      }
      context.emit('invalid', dateInvalid.value);
    }

    const timezoneOffset = computed<number>({
      get() {
        if (value.value) {
          return moment.parseZone(value.value).utcOffset();
        }
        if (!utc.value && allowTimezoneChange.value && timezoneOverwrite.value !== undefined) {
          return timezoneOverwrite.value;
        }
        if (!smtOffset.value || utc.value) {
          return 0;
        }
        return moment().utcOffset(smtOffset.value, true).utcOffset();
      },
      set(newTimezoneOffset) {
        if (utc.value || !allowTimezoneChange.value) {
          return;
        }
        if (!dateInvalid.value) {
          updateDate(dateTimeFormatted.value, newTimezoneOffset);
        } else {
          timezoneOverwrite.value = newTimezoneOffset;
        }
      },
    });

    const dateTimeFormatted = computed<string>({
      get() {
        if (!value.value) {
          return '';
        }
        return format(value.value, momentFormat.value, disableRounding.value);
      },
      set(date: string) {
        updateDate(date, timezoneOffset.value);
      },
    });

    const timezoneElement = useFeathers()
      .get('localCache')
      .getProtocolContentType('f49a8d32-60a4-5e62-b067-f3a853c98806').schema[0] as AutoCompleteElement;
    const timezones = timezoneElement.items.map((item) => {
      if (typeof item.value === 'string') {
        return {
          value: moment().utcOffset(item.value, true).utcOffset(),
          text: item.label,
        };
      }
      return {
        value: item.value,
        text: item.label,
      };
    });

    return {
      dateInvalid,
      dateTimeFormatted,
      internalPlaceholder,
      timezoneOffset,
      timezones,
      smtOffset,
      timezoneOverwrite,
    };
  },
});
</script>
